import axios from 'axios';
import { API_URL, END_POINT_ADMIN } from '../const';

const SUPER_ADMIN_SESSION_KEY = 'quoteSuperAdminSession';
const requestOptions = token => ({
  headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  timeout: 15000,
});
const roleName = admin => admin?.role_name || admin?.role?.name;

export const readAuthSession = () => {
  try {
    return {
      token: sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '') || null,
      admin: JSON.parse(sessionStorage.getItem('quoteadmin') || 'null'),
    };
  } catch {
    return { token: null, admin: null };
  }
};

export const readSuperAdminSession = () => {
  try {
    const session = JSON.parse(sessionStorage.getItem(SUPER_ADMIN_SESSION_KEY) || 'null');
    return session?.token && session.admin?.id && roleName(session.admin) === 'Super Admin' ? session : null;
  } catch {
    return null;
  }
};

const writeSession = ({ token, admin }) => {
  sessionStorage.setItem('quotetoken', token);
  sessionStorage.setItem('quoteloginusername', JSON.stringify(admin.name));
  sessionStorage.setItem('quoteadmin', JSON.stringify(admin));
};

export const clearAuthSession = () => {
  ['quotetoken', 'quoteloginusername', 'quoteadmin', SUPER_ADMIN_SESSION_KEY]
    .forEach(key => sessionStorage.removeItem(key));
};

export const saveLoginSession = (session) => {
  sessionStorage.removeItem(SUPER_ADMIN_SESSION_KEY);
  writeSession(session);
};

const stopVendorToken = async token => {
  try {
    const { data } = await axios.post(API_URL + 'impersonation/stop', {}, requestOptions(token));
    if (!data.status) throw new Error(data.message || 'Unable to end the vendor session.');
  } catch (error) {
    // An expired or already-revoked vendor token cannot be used again.
    if (error.response?.status !== 401) throw error;
  }
};

export const startVendorSession = async (adminId) => {
  const original = readAuthSession();
  if (!original.token || roleName(original.admin) !== 'Super Admin' || readSuperAdminSession()) {
    throw new Error('Only Super Admin can log in as a vendor.');
  }
  const { data } = await axios.post(
    `${API_URL}${END_POINT_ADMIN}/${adminId}/impersonate`, {}, requestOptions(original.token)
  );
  if (!data.status || !data.token || String(data.admin?.id) !== String(adminId) ||
      !data.admin?.vendor_id || data.admin.account_type !== 'vendor' || data.admin.is_vendor_owner !== true ||
      !roleName(data.admin) || roleName(data.admin) === 'Super Admin') {
    throw new Error(data.message || 'Unable to log in as this vendor.');
  }
  if (readAuthSession().token !== original.token) {
    await stopVendorToken(data.token);
    throw new Error('Your session changed. Please log in again.');
  }
  try {
    sessionStorage.setItem(SUPER_ADMIN_SESSION_KEY, JSON.stringify(original));
    writeSession({ token: data.token, admin: data.admin });
  } catch (error) {
    sessionStorage.removeItem(SUPER_ADMIN_SESSION_KEY);
    writeSession(original);
    await stopVendorToken(data.token);
    throw error;
  }
};

export const returnToSuperAdmin = async () => {
  const original = readSuperAdminSession();
  const current = readAuthSession();
  if (!original || !current.token || current.token === original.token) {
    throw new Error('The saved Super Admin session is unavailable. Please sign out and log in again.');
  }

  let restoredAdmin;
  try {
    const { data } = await axios.get(API_URL + 'auth/context', requestOptions(original.token));
    if (data.status && data.data?.is_super_admin &&
        String(data.data.admin?.id) === String(original.admin.id)) {
      restoredAdmin = { ...data.data.admin, role_name: 'Super Admin' };
    }
  } catch (error) {
    if (![401, 403].includes(error.response?.status)) throw error;
  }

  await stopVendorToken(current.token);
  if (!restoredAdmin || readAuthSession().token !== current.token || !readSuperAdminSession()) {
    clearAuthSession();
    return false;
  }
  writeSession({ token: original.token, admin: restoredAdmin });
  sessionStorage.removeItem(SUPER_ADMIN_SESSION_KEY);
  return true;
};

export const signOutSession = async () => {
  const current = readAuthSession();
  const original = readSuperAdminSession();
  const requests = [];
  if (current.token) {
    requests.push(original ? stopVendorToken(current.token) :
      axios.post(API_URL + 'logout', {}, requestOptions(current.token)));
  }
  if (original?.token && original.token !== current.token) {
    requests.push(axios.post(API_URL + 'logout', {}, requestOptions(original.token)));
  }
  try {
    await Promise.allSettled(requests);
  } finally {
    clearAuthSession();
  }
};

// Reload so mounted components cannot retain another account's data or token.
export const reloadSessionPage = path => window.location.replace(path);
